import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import {
    deltaValue,
    getBaseValue,
    KpiReport,
    // getKpiReportFromGQResult, isUnitDollar, KpiReport, KpiReportEntity,
    roundValue,
    Unit
} from "../processors";
// import { queryEntities } from "../misc";
import { FigureHorStackBarKpiProvider, FigureSolidDashKpiProvider } from "./categoryChartData.types";
import { getKpisForProvidersWithCategoryChart } from "../../Components/opendata";
import { appMedian, EMPTY_ARRAY, extractInitialYearWith1DimValues, VALUE_STATUS_NAMING, WaterProvider } from "../misc";
import { figureDeltaString } from "./figureDeltaString";
import _ from "lodash";
import { getConnectionSizeMedianText } from "../../utils";

export const getRealERateReturn = (
    provider: WaterProvider,
    kpis: string[][],
    kpiResults: KpiReport[],
    ignoreProvider: boolean = false
) => {
    const results = ignoreProvider ? kpiResults : kpiResults.filter(d => d?.provider?.tid === provider?.tid);
    if (kpis?.length <= 2 || (!ignoreProvider && !provider) || !results?.length) return null;

    const totalRev = kpis?.[0];
    const fixedAssetCost = kpis?.[1];

    const operatingCosts = kpis?.filter((__, index) => index > 1);
    const operatingCostKpiCodesMap = operatingCosts.reduce((prev, cur) => {
        prev[cur?.[0]] = 1;
        return prev;
    }, {});

    // const fixedAssetCosts = kpis?.[2];

    const _results = results?.map(d => {
        return {
            ...d,
            ...getBaseValue(d.value, d.unit)
        };
    });

    const totalRevResult = _results?.find(d => d.kpiCode === totalRev?.[0]);
    const operatingCostResults = _results
        ?.filter(
            d =>
                // d.kpiCode === operatingCost?.[0]
                operatingCostKpiCodesMap?.[d.kpiCode] === 1 && d.value != null && typeof d.value === "number"
        )
        ?.map(d => d.value);
    const operatingCostResult =
        operatingCostResults?.length > 0
            ? { value: _.sum(operatingCostResults) }
            : {
                  value: null
              };

    const fixedAssetCostResult = _results?.find(d => d.kpiCode === fixedAssetCost?.[0]);

    if (totalRevResult?.value == null || operatingCostResult?.value == null || fixedAssetCostResult?.value == null)
        return null;

    const totalRevValue = totalRevResult?.value;
    const operatingCostValue = operatingCostResult?.value;
    const fixedAssetCostValue = fixedAssetCostResult?.value;

    if (fixedAssetCostValue === 0) return null;

    const ret = ((totalRevValue - operatingCostValue) * 100) / fixedAssetCostValue;
    return ret;
};

export const getMedianRealERateReturn = (kpis: string[][], kpiResults: KpiReport[]) => {
    const realRates = _.values(_.groupBy(kpiResults ?? (EMPTY_ARRAY as KpiReport[]), d => d?.provider?.tid))?.map(
        results => {
            return getRealERateReturn(null, kpis, results, true);
        }
    );

    return appMedian(realRates, "%");

    // if (kpis?.length !== 3 || !kpiResults?.length) return null;

    // const totalRev = kpis?.[0];
    // const operatingCost = kpis?.[1];
    // const fixedAssetCosts = kpis?.[2];

    // const totalRevResult = kpiResults.filter(d => d.kpiCode === totalRev?.[0]);
    // const operatingCostResult = kpiResults.filter(d => d.kpiCode === operatingCost?.[0]);
    // const fixedAssetCostResult = kpiResults.filter(d => d.kpiCode === fixedAssetCosts?.[0]);

    // if (!totalRevResult?.length || !operatingCostResult?.length || !fixedAssetCostResult?.length) return null;

    // let totalRevValue = _.meanBy(totalRevResult, d => d.value);
    // let operatingCostValue = _.meanBy(operatingCostResult, d => d.value);
    // let fixedAssetCostValue = _.meanBy(fixedAssetCostResult, d => d.value);

    // totalRevValue = getValue(totalRevValue ?? 0, totalRevResult?.[0]?.unit as Unit);
    // operatingCostValue = getValue(operatingCostValue ?? 0, operatingCostResult?.[0]?.unit as Unit);
    // fixedAssetCostValue = getValue(fixedAssetCostValue ?? 0, fixedAssetCostResult?.[0]?.unit as Unit);

    // if (fixedAssetCostValue === 0) return null;

    // const ret = ((totalRevValue - operatingCostValue) * 100) / fixedAssetCostValue;
    // return ret;
};

export const fetchFigureSolidDashRealERateReturnKpiProviderChartData: CategoryChartQueryFn = async ({
    metadata,
    categoryChart,
    providers,
    kpis,
    category
}: CategoryChartQueryParam): Promise<FigureHorStackBarKpiProvider> => {
    const provider = providers?.[0];
    const kpi = kpis?.[0];
    if (!metadata || !provider || !categoryChart) return null;

    let kpiReportResults = await getKpisForProvidersWithCategoryChart(kpis, [provider], true, categoryChart);
    if (!kpiReportResults) return null;

    const unit: Unit = "%";

    // group by year
    const groupByYearKpiReportResults = _.groupBy(kpiReportResults, d => d.year);

    let years = _.orderBy(_.uniq(kpiReportResults.map(d => d.year)), d => d, "asc");
    const values =
        years
            ?.map(year => {
                const reports = groupByYearKpiReportResults[year];
                const conSizeCategoryProviders = reports?.filter(
                    d => d?.provider?.connectionSizeCategory === provider?.connectionSizeCategory
                );
                const providerValue = getRealERateReturn(provider, kpis, reports);
                const medianProviderValue = getMedianRealERateReturn(kpis, conSizeCategoryProviders);
                return {
                    year: year,
                    providerValue,
                    providerValueStatus:
                        providerValue != null
                            ? null
                            : reports?.find(d => d?.provider?.tid === provider?.tid)?.valueStatus,
                    medianProviderValue
                };
            })
            ?.filter(d => d?.providerValue != null || d?.providerValueStatus != null) ?? [];

    years = values.map(d => d.year);

    const { displayTitle, visType, figureHeader, figureFooter, visHeader, visFooter } = categoryChart;

    // const largeProviderAverageSeriesName = "Large provider median";
    const providerMedianSeriesName = getConnectionSizeMedianText(metadata, provider?.connectionSizeCategory);

    const averages = values.map(d => d.medianProviderValue);
    const providerValues = values.map(d => d.providerValue);

    const measure = kpi?.[2] ?? "Economic real rate of return";
    const lineDataSeriesName = provider?.name;
    const latestYear = years?.[years.length - 1];
    const latestYearValue = values?.[values.length - 1]?.providerValue;
    const latestYearValueStatus = values?.[values.length - 1]?.providerValueStatus;
    const previousYearValue = values?.[values.length - 2]?.providerValue;

    if (latestYear == null) return null;

    // find delta of values between latest year and previous year
    const delta = deltaValue(previousYearValue, latestYearValue, unit);
    const deltaString = figureDeltaString(delta, category);

    // const fawStyle = `style="color: ${getColor(category?.theme)}"`;
    // const deltaString = `${
    //     delta === 0
    //         ? `<span class='fa-solid fa-right mr-1' ${fawStyle}></span> No change from last year`
    //         : `${
    //               delta > 0
    //                   ? `<span class='fa-solid fa-up mr-1' ${fawStyle}></span>`
    //                   : delta < 0
    //                   ? `<span class='fa-solid fa-down mr-1' ${fawStyle}></span>`
    //                   : ""
    //           } ${delta}% ${delta > 0 ? "above" : "below"} last year`
    // }`;

    // create content for header and footer of the figure
    const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
        year: latestYear,
        value: latestYearValue,
        deltaString
    });

    const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
        year: latestYear,
        value: latestYearValue,
        deltaString
    });

    const initialYear = extractInitialYearWith1DimValues(years, providerValues, averages);

    // create chart data
    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const ret: FigureSolidDashKpiProvider = {
        unit: unit as Unit,
        year: latestYear,
        waterProvider: provider,
        kpiCode: kpi[0],
        schemeType: kpi[1],
        title: displayTitle,
        footer: null,
        figure: !(visType === "figure" || visType === "figureWithSolidDashLine")
            ? null
            : {
                  decimals:
                      latestYearValue != null ? roundValue(latestYearValue, unit as Unit, true, false).decimals : 0,
                  header:
                      !figureHeader || figureHeader.processed.trim() === ""
                          ? null
                          : {
                                processed: figureHeaderProcessed,
                                value: figureHeaderProcessed,
                                format: figureHeaderProcessed
                            },
                  value:
                      latestYearValue != null
                          ? roundValue(latestYearValue, unit as Unit, true, false).value
                          : latestYearValueStatus != null
                          ? VALUE_STATUS_NAMING?.[latestYearValueStatus] ?? latestYearValueStatus
                          : previousYearValue != null
                          ? VALUE_STATUS_NAMING.MD
                          : VALUE_STATUS_NAMING.NR,
                  footer:
                      !figureFooter || figureFooter.processed.trim() === ""
                          ? null
                          : {
                                processed: figureFooterProcessed,
                                value: figureFooterProcessed,
                                format: figureFooterProcessed
                            }
              },
        graph:
            visType === "figure"
                ? null
                : {
                      measure,
                      years,
                      lineData: (providerValues ?? []).map(d => roundValue(d, unit, true, false)?.value),
                      providerMedianData: (averages ?? []).map(d => roundValue(d, unit, true, false)?.value),
                      providerMedianSeriesName,
                      providerMedianAnnotation: providerMedianSeriesName,

                      //   largeProviderAverageSeriesName: `${largeProviderAverageSeriesName}`,
                      //   largeProviderAverageData: (averages ?? []).map(d => roundValue(d, unit)?.value),

                      lineDataSeriesName: `${lineDataSeriesName}`,
                      header:
                          !visHeader || visHeader.processed.trim() === ""
                              ? null
                              : {
                                    processed: visHeaderProcessed,
                                    value: visHeaderProcessed,
                                    format: visHeaderProcessed
                                },
                      footer:
                          !visFooter || visFooter.processed.trim() === ""
                              ? null
                              : {
                                    processed: visFooterProcessed,
                                    value: visFooterProcessed,
                                    format: visFooterProcessed
                                }
                  }
    };

    return ret;
};
